import { FormEvent, useEffect, useMemo, useState } from "react";
import { FileLock2, FileUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import { useRoleSimulator } from "@/security/RoleSimulatorProvider";
import type { AccessState, DocumentRecord, FarmDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function DocumentsPage() {
  const offline = useOffline();
  const { can } = useRoleSimulator();
  const [items, setItems] = useState<DocumentRecord[]>();
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [authorized, setAuthorized] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [referenceError, setReferenceError] = useState("");
  const [form, setForm] = useState({
    entityType: "FARMER" as DocumentRecord["entityType"],
    entityId: "",
    category: "",
    fileName: "",
    mimeType: "application/octet-stream",
    sizeBytes: 0,
  });
  const [error, setError] = useState("");

  const loadPage = async () => {
    setReferenceError("");
    try {
      const [documentResult, farmResult] = await Promise.all([
        services.media.list({ pageSize: 100 }),
        services.farms.list({ pageSize: 100 }),
      ]);
      setItems(documentResult.items);
      setFarms(farmResult.items);
    } catch (reason: unknown) {
      setReferenceError(reason instanceof Error ? reason.message : "Document reference data could not be loaded.");
    }
  };

  useEffect(() => { void loadPage(); }, []);

  const entityOptions = useMemo(() => {
    if (form.entityType === "FARMER") {
      const farmers = new Map<string, string>();
      farms.forEach((farm) => {
        if (!farmers.has(farm.farmerId)) farmers.set(farm.farmerId, farm.farmerName);
      });
      return Array.from(farmers, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
    }
    if (form.entityType === "FARM") {
      return farms
        .map((farm) => ({ id: farm.id, label: `${farm.name} · ${farm.farmerName}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return farms
      .flatMap((farm) => (farm.plotsData || []).map((plot) => ({
        id: plot.id,
        label: `${plot.name} · ${farm.name} · ${farm.farmerName}`,
      })))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [farms, form.entityType]);

  if (referenceError && !items) return <ErrorState message={referenceError} onRetry={loadPage} />;
  if (!items) return <LoadingState label="Loading document metadata" />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!authorized) {
      setError("Permission denied. Contact your administrator for access.");
      return;
    }
    if (!form.entityId || !form.category || !form.fileName) {
      setError("Linked entity, document category, and file are required.");
      return;
    }
    const selectedEntity = entityOptions.find((option) => option.id === form.entityId);
    if (!selectedEntity) {
      setError("The selected linked entity is no longer available. Refresh the reference data and try again.");
      return;
    }

    setError("");
    setUploading(true);
    setProgress(35);
    const entityId = crypto.randomUUID();
    const isOffline = offline.simulatedOffline || !offline.online;
    const domainInput = {
      entityType: form.entityType,
      entityId: form.entityId,
      entityLabel: selectedEntity.label,
      documentType: form.category,
      bucketId: "farmer-documents" as const,
      fileName: form.fileName,
      mimeType: form.mimeType,
      sizeBytes: form.sizeBytes,
      uploadedBy: "Current user",
      confidential: false,
      accessState: "AUTHORIZED" as const,
    };
    const apiPayload = {
      client_generated_id: entityId,
      entity_type: domainInput.entityType,
      entity_id: toApiId(domainInput.entityId),
      document_type: domainInput.documentType,
      bucket_id: domainInput.bucketId,
      file_name: domainInput.fileName,
      mime_type: domainInput.mimeType,
      size_bytes: domainInput.sizeBytes,
      confidential: domainInput.confidential,
    };
    const result = await executeOrQueue({
      offline: isOffline,
      request: { entityType: "DOCUMENT_METADATA", entityId, method: "POST", path: "/documents", payload: apiPayload },
      execute: () => services.media.createMock(domainInput, entityId),
    });
    setProgress(100);
    setUploading(false);
    if (result.mode === "SYNCED") setItems([result.value, ...items]);
    setForm((current) => ({ ...current, entityId: "", category: "", fileName: "", mimeType: "application/octet-stream", sizeBytes: 0 }));
    toast(result.mode === "QUEUED" ? "Document metadata queued" : "Document metadata registered", {
      description: result.mode === "QUEUED"
        ? "Only metadata is queued; no file bytes were stored."
        : "Metadata saved against the selected live ARISE record.",
    });
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <PageHeader
        eyebrow="Private media architecture"
        title="Documents & Media"
        description="Register document metadata against live Farmer, Farm or Plot records."
      />
      <Alert data-testid="documents-storage-alert">
        <FileLock2 className="size-4" />
        <AlertTitle>No file uploads</AlertTitle>
        <AlertDescription>The interface currently registers metadata only. File bytes are not uploaded by this workflow.</AlertDescription>
      </Alert>
      {referenceError && <Card className="border-destructive/30"><CardContent className="p-4 text-sm text-destructive">{referenceError}</CardContent></Card>}
      <Card data-testid="documents-upload-panel">
        <CardContent className="p-5">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold">Document registration</p>
              <p className="text-xs text-muted-foreground">Choose the exact live record that this document belongs to.</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="document-authorized-mode">Authorized</Label>
              <Switch id="document-authorized-mode" checked={authorized} onCheckedChange={setAuthorized} data-testid="documents-authorization-toggle" />
            </div>
          </div>
          {!authorized && (
            <div className="mb-4 flex gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning" data-testid="documents-unauthorized-state">
              <ShieldAlert className="size-4" />A media.create permission and exact-path registration would be required.
            </div>
          )}
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-4" data-testid="documents-upload-form">
            <div className="space-y-2">
              <Label>Entity type</Label>
              <Select
                value={form.entityType}
                onValueChange={(value: DocumentRecord["entityType"]) => setForm((current) => ({ ...current, entityType: value, entityId: "" }))}
              >
                <SelectTrigger data-testid="documents-entity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FARMER">Farmer</SelectItem>
                  <SelectItem value="FARM">Farm</SelectItem>
                  <SelectItem value="PLOT">Plot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Linked entity</Label>
              <Select value={form.entityId} onValueChange={(value: string) => setForm((current) => ({ ...current, entityId: value }))}>
                <SelectTrigger data-testid="documents-entity-select"><SelectValue placeholder={entityOptions.length ? `Select ${form.entityType.toLowerCase()}` : "No matching records"} /></SelectTrigger>
                <SelectContent>{entityOptions.map((option) => <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="document-category">Category</Label>
              <Input
                id="document-category"
                value={form.category}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, category: e.target.value }))}
                placeholder="e.g. Land reference"
                data-testid="documents-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="document-file">File</Label>
              <Input
                id="document-file"
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0];
                  setForm((current) => ({
                    ...current,
                    fileName: file?.name || "",
                    mimeType: file?.type || "application/octet-stream",
                    sizeBytes: file?.size || 0,
                  }));
                }}
                data-testid="documents-upload-file-input"
              />
            </div>
            {error && <p className="text-sm text-destructive md:col-span-4" data-testid="documents-upload-error">{error}</p>}
            {uploading && (
              <div className="space-y-2 md:col-span-4">
                <Progress value={progress} data-testid="documents-upload-progress" />
                <p className="text-xs text-muted-foreground">Registering metadata · {progress}%</p>
              </div>
            )}
            <div className="md:col-span-4">
              <Button type="submit" disabled={uploading || !!referenceError || !can("media.create")} data-testid="documents-upload-submit-button">
                <FileUp className="mr-2 size-4" />Register document
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table data-testid="documents-table">
            <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>Linked entity</TableHead><TableHead>Bucket reference</TableHead><TableHead>Uploaded</TableHead><TableHead>Access</TableHead></TableRow></TableHeader>
            <TableBody>{items.map((item) => (
              <TableRow key={item.id}>
                <TableCell><p className="font-semibold">{item.documentType}</p><p className="text-xs text-muted-foreground">{item.fileName} · {(item.sizeBytes / 1000).toFixed(0)} KB</p></TableCell>
                <TableCell>{item.entityLabel}<p className="text-xs text-muted-foreground">{item.entityType}</p></TableCell>
                <TableCell className="font-mono text-xs">{item.bucketId}</TableCell>
                <TableCell className="text-xs">{item.uploadedAt.slice(0, 10)}<p className="text-muted-foreground">{item.uploadedBy}</p></TableCell>
                <TableCell><StatusBadge status={item.accessState as AccessState} testId={`document-status-${item.id}`} /></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
